import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Clock, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";

const stats = [
{ icon: Clock, value: "25+", label: "YEARS EXPERIENCE" },
{ icon: Wrench, value: "100+", label: "PROJECTS COMPLETED" },
{ icon: Users, value: "BUILT", label: "DREAMS REALIZED" },
{ icon: Award, value: "SEMA", label: "& CAR SHOWS" }];


export default function AboutPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-24 bg-zinc-950">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1579103769354-8796259d8d54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHx3ZWxkaW5nJTIwZ2FyYWdlJTIwbWVjaGFuaWMlMjBhdXRvfGVufDB8fHx8MTc3NTgzODE0Nnww&ixlib=rb-4.1.0&q=85"
            alt="Workshop"
            className="w-full h-full object-cover opacity-30" />

          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/70" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl">

            <span className="font-mono text-xs tracking-[0.3em] text-amber-500 uppercase mb-4 block">
              OUR STORY
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl text-white tracking-tighter mb-6">
              BUILT ON<br />
              <span className="text-amber-500">PASSION</span>
            </h1>
            <p className="font-body text-lg text-zinc-300 leading-relaxed">The passion was born while working in a single car garage working on an old truck with my dad and has grown into a full-service custom build, specialty coatings, and 3D design & parts fabrication facility. 

            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-amber-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="text-center">

                <stat.icon size={28} className="text-black mx-auto mb-2" />
                <div className="font-heading text-4xl sm:text-5xl text-black">{stat.value}</div>
                <div className="font-mono text-xs tracking-wide text-black/70">{stat.label}</div>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 bg-[#09090b]" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <div className="aspect-[4/3] overflow-hidden border border-white/10">
                <img
                  src="https://customer-assets.emergentagent.com/job_hot-rod-coatings/artifacts/vfjbopgv_Gemini_Generated_Image_vzs0y4vzs0y4vzs0.png"
                  alt="Vintage gas station with a classic Chevy C10 — honoring the Martens Motor Service legacy"
                  className="w-full h-full object-cover" />

              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}>

              <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">
                FROM GARAGE TO<br />
                <span className="text-amber-500">INDUSTRY LEADER</span>
              </h2>
              
              <div className="space-y-4 font-body text-zinc-400 leading-relaxed">
                <p>It all started with wanting to honor the ones who came before us and instilled the love of cars and doing things right. Martens Motor Service was a shop in Yarrow BC, which is where we live today. My grandpa drove the oil truck here and my dad worked here as a kid. Martens Motor Co is about coming together as family and community and doing what we love together.  

                </p>
                <p>
                  Our philosophy is simple: treat every car like it's our own. We're enthusiasts first, craftsmen second. Every bolt we turn, every weld we lay, every coat we spray is done with the same passion we'd put into our personal builds.
                </p>
                <p>Over the years, we've built a reputation for quality, integrity, and attention to detail. Our clients aren't just customers—they're fellow gearheads and friends who trust us with their prized possessions.

                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-zinc-950 border-t border-white/10" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16">

            <span className="font-mono text-xs tracking-[0.2em] text-zinc-500 uppercase">
              WHAT DRIVES US
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mt-2">
              OUR <span className="text-amber-500">VALUES</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {[
            {
              title: "CRAFTSMANSHIP",
              description: "Every project receives our full attention. We don't cut corners, and we don't rush. Quality takes time, and we take the time to do it right. ♪ Anything less than our best is a felony."
            },
            {
              title: "TRANSPARENCY",
              description: "No hidden fees, no surprises. We strive to communicate openly throughout the build process, keeping you informed every step of the way."
            },
            {
              title: "PASSION",
              description: "We do this because we love it. That passion shows in every detail of our work. Your car isn't just a project to us—it's a labor of love."
            }].
            map((value, index) =>
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-zinc-950 p-8 md:p-12">

                <h3 className="font-heading text-2xl text-amber-500 mb-4">{value.title}</h3>
                <p className="font-body text-zinc-400 leading-relaxed">{value.description}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <h2 className="font-heading text-4xl sm:text-5xl text-white tracking-tight mb-6">
              READY TO MEET<br />
              <span className="text-amber-500">THE TEAM?</span>
            </h2>
            <p className="font-body text-lg text-zinc-300 mb-8">Have questions about a project? Want to schedule a shop visit? Drop us a line and we'll get back to you as soon as possible.

            </p>
            <Link to="/contact">
              <Button
                className="bg-amber-500 text-black font-heading text-xl tracking-wide px-10 py-6 rounded-none hover:bg-amber-400 transition-all duration-200"
                data-testid="about-cta-btn">

                GET IN TOUCH
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>);

}